import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import UTIL from './util';

const motionDefaults = {
  motionSpanVh: 100,
  startYVh: 110,
  endYVh: -20,
  triggerLeadVh: 15,
  fadeInStart: 0.15,
  fadeInEnd: 0.25,
  fadeOutStart: 0.7,
  fadeOutEnd: 0.8,
  opacityStart: 0,
  opacityEnd: 0,
  blurPx: 10,
  pinTailVh: 100,
};

const fixedDefaults = {
  fadeInStart: 0.32,
  fadeInEnd: 0.45,
  fadeOutStart: 0.7,
  fadeOutEnd: 0.8,
  opacityStart: 0,
  opacityEnd: 0,
};

function clamp01(v) {
  if (v <= 0) {
    return 0;
  }
  if (v >= 1) {
    return 1;
  }
  return v;
}

function numberSetting(el, attrName, fallback) {
  return UTIL.attrNumber(el, attrName, fallback);
}

function fadeWindow(start, end) {
  if (!(end > start)) {
    end = start + 0.001;
  }
  return { start, end };
}

function opacityFromWindows(t, fadeIn, fadeOut, opacityStart, opacityEnd) {
  const fadeInT = clamp01((t - fadeIn.start) / (fadeIn.end - fadeIn.start));
  const fadeOutT = clamp01((t - fadeOut.start) / (fadeOut.end - fadeOut.start));
  const opacity = opacityStart + ((1 - opacityStart) * fadeInT);
  return opacity + ((opacityEnd - opacity) * fadeOutT);
}

function placeholderTopVh(hostSection, vh) {
  const el = hostSection.querySelector('.pause-placeholder');
  if (!el) {
    return null;
  }
  return (el.getBoundingClientRect().top / Math.max(1, vh)) * 100;
}

function yVhToMotionT(targetYVh, startYVh, endYVh) {
  const span = endYVh - startYVh;
  if (Math.abs(span) < 0.001) {
    return null;
  }
  return (targetYVh - startYVh) / span;
}

function offsetMotionT(el, panel, hostSection, startYVh, endYVh, vh, offsetAttr, atBottom) {
  const offsetRaw = UTIL.attr(el, offsetAttr);
  if (offsetRaw == null || String(offsetRaw).trim() === '') {
    return null;
  }
  const phTopVh = placeholderTopVh(hostSection, vh);
  if (phTopVh == null) {
    return null;
  }
  const offsetVh = UTIL.lengthToVh(offsetRaw, panel);
  let targetYVh = phTopVh - offsetVh;
  if (atBottom) {
    targetYVh -= (panel.offsetHeight / Math.max(1, vh)) * 100;
  }
  return yVhToMotionT(targetYVh, startYVh, endYVh);
}

function fadeProgressSetting(el, panel, hostSection, startYVh, endYVh, vh, offsetAttr, progressAttr, fallback, atBottom) {
  const t = offsetMotionT(el, panel, hostSection, startYVh, endYVh, vh, offsetAttr, atBottom);
  if (t != null && Number.isFinite(t)) {
    return t;
  }
  return numberSetting(el, progressAttr, fallback);
}

function resolvePause(panel, hostSection, startYVh, endYVh, vh) {
  const pauseRaw = UTIL.attr(panel, 'data-intro-pause-for');
  if (pauseRaw == null || String(pauseRaw).trim() === '') {
    return null;
  }
  const pauseVh = UTIL.lengthToVh(pauseRaw, panel);
  if (!(pauseVh > 0)) {
    return null;
  }
  const pauseAtRaw = UTIL.attr(panel, 'data-intro-pause-at');
  const pauseAtNorm = pauseAtRaw == null ? '' : String(pauseAtRaw).trim().toLowerCase();
  let t;
  if (pauseAtNorm !== '' && pauseAtNorm !== 'bottom') {
    t = parseFloat(pauseAtNorm);
    if (!Number.isFinite(t)) {
      return null;
    }
  } else {
    t = offsetMotionT(panel, panel, hostSection, startYVh, endYVh, vh, 'data-intro-pause-offset', pauseAtNorm === 'bottom');
    if (t == null && pauseAtNorm === 'bottom') {
      t = offsetMotionT(panel, panel, hostSection, startYVh, endYVh, vh, 'data-intro-pause-offset', true);
    }
    if (t == null) {
      const phTopVh = placeholderTopVh(hostSection, vh);
      if (phTopVh == null) {
        return null;
      }
      let targetYVh = phTopVh;
      if (pauseAtNorm === 'bottom') {
        targetYVh -= (panel.offsetHeight / Math.max(1, vh)) * 100;
      }
      t = yVhToMotionT(targetYVh - UTIL.lengthToVh(UTIL.attr(panel, 'data-intro-pause-offset') || '0', panel), startYVh, endYVh);
    }
  }
  if (t == null || !Number.isFinite(t)) {
    return null;
  }
  return {
    t: clamp01(t),
    pauseVh,
  };
}

function applyFixedPanel(fixedPanel, t, ctx) {
  if (t == null) {
    const hidden = clamp01(numberSetting(fixedPanel, 'data-content-fixed-opacity-start', fixedDefaults.opacityStart));
    fixedPanel.style.opacity = String(hidden);
    fixedPanel.style.visibility = hidden <= 0.001 ? 'hidden' : 'visible';
    return;
  }
  const { panel, hostSection, startYVh, endYVh, vh, atBottom } = ctx;
  const fadeIn = fadeWindow(
    fadeProgressSetting(fixedPanel, panel, hostSection, startYVh, endYVh, vh, 'data-content-fixed-fade-in-start-offset', 'data-content-fixed-fade-in-start', fixedDefaults.fadeInStart, atBottom),
    fadeProgressSetting(fixedPanel, panel, hostSection, startYVh, endYVh, vh, 'data-content-fixed-fade-in-end-offset', 'data-content-fixed-fade-in-end', fixedDefaults.fadeInEnd, atBottom),
  );
  const fadeOut = fadeWindow(
    fadeProgressSetting(fixedPanel, panel, hostSection, startYVh, endYVh, vh, 'data-content-fixed-fade-out-start-offset', 'data-content-fixed-fade-out-start', fixedDefaults.fadeOutStart, atBottom),
    fadeProgressSetting(fixedPanel, panel, hostSection, startYVh, endYVh, vh, 'data-content-fixed-fade-out-end-offset', 'data-content-fixed-fade-out-end', fixedDefaults.fadeOutEnd, atBottom),
  );
  const opacityStart = clamp01(numberSetting(fixedPanel, 'data-content-fixed-opacity-start', fixedDefaults.opacityStart));
  const opacityEnd = clamp01(numberSetting(fixedPanel, 'data-content-fixed-opacity-end', fixedDefaults.opacityEnd));
  const opacity = opacityFromWindows(t, fadeIn, fadeOut, opacityStart, opacityEnd);
  fixedPanel.style.opacity = String(opacity);
  fixedPanel.style.visibility = opacity <= 0.001 ? 'hidden' : 'visible';
}

function applyPanel(panel, t, ctx) {
  const yVh = ctx.startYVh + ((ctx.endYVh - ctx.startYVh) * t);
  panel.style.transform = `translate3d(0, ${yVh}svh, 0)`;
  const opacity = opacityFromWindows(t, ctx.fadeIn, ctx.fadeOut, ctx.opacityStart, ctx.opacityEnd);
  const blurPx = ctx.blurPx * (1 - opacity);
  panel.style.opacity = String(opacity);
  panel.style.filter = blurPx > 0.01 ? `blur(${blurPx}px)` : 'none';
  panel.style.visibility = opacity <= 0.001 ? 'hidden' : 'visible';
  const fixedPanels = ctx.hostSection.querySelectorAll('.content-fixed');
  for (let i = 0; i < fixedPanels.length; i++) {
    applyFixedPanel(fixedPanels[i], t, ctx);
  }
}

function applyPanelHidden(panel, ctx) {
  panel.style.transform = `translate3d(0, ${ctx.startYVh}svh, 0)`;
  panel.style.opacity = String(ctx.opacityStart);
  panel.style.filter = ctx.blurPx > 0 && ctx.opacityStart < 1 ? `blur(${ctx.blurPx}px)` : 'none';
  panel.style.visibility = ctx.opacityStart <= 0.001 ? 'hidden' : 'visible';
  const fixedPanels = ctx.hostSection.querySelectorAll('.content-fixed');
  for (let i = 0; i < fixedPanels.length; i++) {
    applyFixedPanel(fixedPanels[i], null, ctx);
  }
}

function motionTFromProgress(progress, motionVh, pause, tailVh) {
  const pauseVh = pause ? pause.pauseVh : 0;
  const pauseAt = pause ? pause.t : 0;
  const totalVh = motionVh + pauseVh + tailVh;
  if (!(totalVh > 0)) {
    return 1;
  }
  const u = progress * totalVh;
  if (!pause || pauseVh <= 0) {
    if (u >= motionVh) {
      return 1;
    }
    return clamp01(u / Math.max(0.001, motionVh));
  }
  const pauseStart = pauseAt * motionVh;
  if (u <= pauseStart) {
    return clamp01(u / Math.max(0.001, motionVh));
  }
  if (u <= pauseStart + pauseVh) {
    return pauseAt;
  }
  const after = u - pauseVh;
  if (after >= motionVh) {
    return 1;
  }
  return clamp01(after / Math.max(0.001, motionVh));
}

function shouldPinSection(section) {
  if (!section) {
    return false;
  }
  if (section.id === 'projects' || (section.parentElement && section.parentElement.id === 'work')) {
    return false;
  }
  if (section.id === 'case-hero') {
    return true;
  }
  if (section.closest && section.closest('#case-study')) {
    return true;
  }
  return false;
}

function triggerForPanel(panel, section) {
  if (shouldPinSection(section)) {
    return section;
  }
  const selector = UTIL.attr(panel, 'data-intro-trigger');
  if (selector) {
    const el = document.querySelector(selector);
    if (el) {
      return el;
    }
  }
  const work = document.getElementById('work');
  if (work && section.id === 'projects' && work.contains(section)) {
    return work;
  }
  return section;
}

function buildPanelContext(panel) {
  const hostSection = panel.closest('section');
  if (!hostSection) {
    return null;
  }
  const vh = UTIL.svh() || 1;
  let startYVh = numberSetting(panel, 'data-intro-start-y-vh', motionDefaults.startYVh);
  let endYVh = numberSetting(panel, 'data-intro-end-y-vh', motionDefaults.endYVh);
  if (!Number.isFinite(startYVh)) {
    startYVh = motionDefaults.startYVh;
  }
  if (!Number.isFinite(endYVh)) {
    endYVh = motionDefaults.endYVh;
  }
  const pauseAtRaw = UTIL.attr(panel, 'data-intro-pause-at');
  const atBottom = pauseAtRaw != null && String(pauseAtRaw).trim().toLowerCase() === 'bottom';
  const fadeIn = fadeWindow(
    fadeProgressSetting(panel, panel, hostSection, startYVh, endYVh, vh, 'data-intro-fade-in-start-offset', 'data-intro-fade-in-start', motionDefaults.fadeInStart, atBottom),
    fadeProgressSetting(panel, panel, hostSection, startYVh, endYVh, vh, 'data-intro-fade-in-end-offset', 'data-intro-fade-in-end', motionDefaults.fadeInEnd, atBottom),
  );
  const fadeOut = fadeWindow(
    fadeProgressSetting(panel, panel, hostSection, startYVh, endYVh, vh, 'data-intro-fade-out-start-offset', 'data-intro-fade-out-start', motionDefaults.fadeOutStart, atBottom),
    fadeProgressSetting(panel, panel, hostSection, startYVh, endYVh, vh, 'data-intro-fade-out-end-offset', 'data-intro-fade-out-end', motionDefaults.fadeOutEnd, atBottom),
  );
  return {
    panel,
    hostSection,
    vh,
    startYVh,
    endYVh,
    fadeIn,
    fadeOut,
    opacityStart: clamp01(numberSetting(panel, 'data-intro-opacity-start', motionDefaults.opacityStart)),
    opacityEnd: clamp01(numberSetting(panel, 'data-intro-opacity-end', motionDefaults.opacityEnd)),
    blurPx: Math.max(0, numberSetting(panel, 'data-intro-blur-px', motionDefaults.blurPx)),
    motionSpanVh: numberSetting(panel, 'data-intro-motion-span-vh', motionDefaults.motionSpanVh) || motionDefaults.motionSpanVh,
    triggerLeadVh: numberSetting(panel, 'data-intro-trigger-lead-vh', motionDefaults.triggerLeadVh),
    pause: resolvePause(panel, hostSection, startYVh, endYVh, vh),
    atBottom,
  };
}

function bindContentScroll(triggers) {
  const seen = [];
  const panels = document.querySelectorAll('.content-scroll');
  for (let i = 0; i < panels.length; i++) {
    const panel = panels[i];
    let ctx = buildPanelContext(panel);
    if (!ctx) {
      continue;
    }
    const section = ctx.hostSection;
    if (seen.indexOf(section) !== -1) {
      continue;
    }
    seen.push(section);

    const wrap = section.parentElement && section.parentElement.classList.contains('content-scroll-pin-wrap')
      ? section.parentElement
      : null;
    const trigger = wrap || triggerForPanel(panel, section);

    function currentCtx() {
      ctx = buildPanelContext(panel) || ctx;
      return ctx;
    }

    function applyFromProgress(progress) {
      const c = currentCtx();
      const pauseVh = c.pause ? c.pause.pauseVh : 0;
      let tailVh = 0;
      if (wrap) {
        const totalVh = (wrap.offsetHeight / Math.max(1, UTIL.scrollVh(panel))) * 100;
        tailVh = Math.max(0, totalVh - c.motionSpanVh - pauseVh);
      }
      const t = motionTFromProgress(progress, c.motionSpanVh, c.pause, tailVh);
      applyPanel(panel, t, c);
    }

    triggers.push(ScrollTrigger.create({
      trigger,
      start: 'top top',
      end: wrap ? 'bottom top' : function () {
        const c = currentCtx();
        const pauseVh = c.pause ? c.pause.pauseVh : 0;
        return '+=' + ((c.motionSpanVh + pauseVh) / 100) * UTIL.scrollVh(panel);
      },
      pin: false,
      scrub: true,
      invalidateOnRefresh: true,
      onUpdate(self) {
        applyFromProgress(self.progress);
      },
      onLeaveBack() {
        applyPanelHidden(panel, currentCtx());
      },
      onLeave() {
        applyFromProgress(1);
      },
    }));
    applyPanelHidden(panel, ctx);
  }
}

function bindScrollY(triggers) {
  const hosts = document.querySelectorAll('[data-scroll-y-start-vh], [data-scroll-start]');
  for (let i = 0; i < hosts.length; i++) {
    const host = hosts[i];

    function isReverse() {
      const reverseRaw = UTIL.attr(host, 'data-scroll-y-reverse');
      if (reverseRaw == null) {
        return false;
      }
      const reverseNorm = String(reverseRaw).trim().toLowerCase();
      return reverseNorm === '' || reverseNorm === 'true' || reverseNorm === '1' || reverseNorm === 'reverse';
    }

    function triggerEl() {
      const selector = UTIL.attr(host, 'data-scroll-y-trigger');
      let trigger = selector ? document.querySelector(selector) : host;
      if (!trigger) {
        trigger = host;
      }
      if (trigger.parentElement && trigger.parentElement.classList.contains('content-scroll-pin-wrap')) {
        return trigger.parentElement;
      }
      if (window.getComputedStyle(trigger).position === 'fixed' && trigger.parentElement) {
        return trigger.parentElement;
      }
      return trigger;
    }

    function startValue() {
      const startPreset = UTIL.attr(host, 'data-scroll-start');
      const preset = startPreset == null ? '' : String(startPreset).trim().toLowerCase();
      let startYVh = UTIL.attrNumber(host, 'data-scroll-y-start-vh');
      if (startYVh == null) {
        startYVh = 0;
      }
      let offsetPct = UTIL.attrNumber(host, 'data-scroll-y-start-offset');
      if (offsetPct == null) {
        offsetPct = 0;
      }
      if (offsetPct < -100) {
        offsetPct = -100;
      }
      if (offsetPct > 100) {
        offsetPct = 100;
      }
      const vh = UTIL.svh();
      const trigger = triggerEl();
      let extraPx = ((startYVh / 100) * vh) + ((offsetPct / 100) * Math.max(1, trigger.offsetHeight));
      let view = 'top';
      if (preset === 'late') {
        view = '25%';
      } else if (preset === 'early' || (preset && !/^(complete|late)$/.test(preset) && !Number.isFinite(parseFloat(preset)))) {
        view = '75%';
      } else if (preset && Number.isFinite(parseFloat(preset)) && !/^(early|late|complete)$/.test(preset)) {
        extraPx += -(parseFloat(preset) / 100) * vh;
      }
      if (extraPx > 1) {
        return `top+=${Math.round(extraPx)}px ${view}`;
      }
      if (extraPx < -1) {
        return `top-=${Math.round(-extraPx)}px ${view}`;
      }
      if (view === 'top') {
        return 'top top';
      }
      return `top ${view}`;
    }

    const startYVh = UTIL.attrNumber(host, 'data-scroll-y-start-vh');
    if (startYVh == null && !host.hasAttribute('data-scroll-start')) {
      continue;
    }

    host.style.setProperty('--scroll-y', isReverse() ? '100' : '0');

    triggers.push(ScrollTrigger.create({
      trigger() {
        return triggerEl();
      },
      start: startValue,
      end() {
        let spanVh = UTIL.attrNumber(host, 'data-scroll-y-span-vh');
        if (spanVh == null || spanVh <= 0) {
          spanVh = 100;
        }
        let factor = UTIL.attrNumber(host, 'data-scroll-y-factor');
        if (factor == null || factor <= 0) {
          factor = 1;
        }
        return '+=' + (spanVh / 100 / factor) * UTIL.scrollVh(host);
      },
      scrub: 0.25,
      invalidateOnRefresh: true,
      onUpdate(self) {
        let value = self.progress * 100;
        if (isReverse()) {
          value = 100 - value;
        }
        host.style.setProperty('--scroll-y', String(value));
      },
    }));
  }
}

function bindStandaloneFixed(triggers) {
  const seen = [];
  const panels = document.querySelectorAll('.content-fixed');
  for (let i = 0; i < panels.length; i++) {
    const section = panels[i].closest('section');
    if (!section || seen.indexOf(section) !== -1) {
      continue;
    }
    if (section.querySelector('.content-scroll')) {
      seen.push(section);
      continue;
    }
    seen.push(section);
    triggers.push(ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end() {
        return '+=' + Math.max(UTIL.svh(), section.offsetHeight);
      },
      scrub: true,
      invalidateOnRefresh: true,
      onUpdate(self) {
        const list = section.querySelectorAll('.content-fixed');
        for (let j = 0; j < list.length; j++) {
          applyFixedPanel(list[j], self.progress, {
            panel: list[j],
            hostSection: section,
            startYVh: 0,
            endYVh: 100,
            vh: UTIL.svh(),
          });
        }
      },
      onLeaveBack() {
        const list = section.querySelectorAll('.content-fixed');
        for (let j = 0; j < list.length; j++) {
          applyFixedPanel(list[j], null, {
            panel: list[j],
            hostSection: section,
            startYVh: 0,
            endYVh: 100,
            vh: UTIL.svh(),
          });
        }
      },
    }));
  }
}

export function initGsapScroll() {
  const triggers = [];
  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ ignoreMobileResize: true });
  bindContentScroll(triggers);
  bindScrollY(triggers);
  bindStandaloneFixed(triggers);
  ScrollTrigger.refresh();
  const unsubSettle = UTIL.onLayoutSettle(function () {
    ScrollTrigger.refresh();
  });

  return function destroy() {
    unsubSettle();
    triggers.forEach((trigger) => trigger.kill());
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
  };
}
